import { create } from 'zustand';
import { normalizeSchemas } from '../utils/schemaTree';

/**
 * Collections + schemas tree + sidebar selection/edit state + request history.
 *
 * Replaces the App-level useState plus the ref-mirrors (collectionsRef,
 * selectedCollectionRef, selectedFolderRef, selectedSchemaEntryRef) that let
 * async handlers (selectRequest, AI, tree-save, mock reload) read the latest
 * values synchronously — `useCollectionsStore.getState()` is the equivalent.
 * The former `collectionsRef.current = next; setCollections(next)` double-writes
 * collapse to a single `setCollections(next)` (store set is synchronous).
 *
 * Setters are exported as stable module-level functions (named identically to
 * the old useState setters); they mirror useState semantics (value or updater).
 */
const makeInitial = () => ({
  collections: [],
  schemas: normalizeSchemas(null),
  selectedRequest: null,
  selectedCollection: null,
  selectedFolder: null,
  editingItem: null,
  editingSchemaItem: null,
  selectedSchemaEntry: null, // { kind, id }
  sidebarFadeInIds: new Set(),
  history: [],
  // Storage namespace `history` was loaded for; null until the first load.
  // See services/historyPersistence.js.
  historyNs: null,
});

const make = (set, key) => (value) =>
  set((state) => ({ [key]: typeof value === 'function' ? value(state[key]) : value }));

export const useCollectionsStore = create((set) => ({
  ...makeInitial(),
  setCollections: make(set, 'collections'),
  setSchemas: make(set, 'schemas'),
  setSelectedRequest: make(set, 'selectedRequest'),
  setSelectedCollection: make(set, 'selectedCollection'),
  setSelectedFolder: make(set, 'selectedFolder'),
  setEditingItem: make(set, 'editingItem'),
  setEditingSchemaItem: make(set, 'editingSchemaItem'),
  setSelectedSchemaEntry: make(set, 'selectedSchemaEntry'),
  setSidebarFadeInIds: make(set, 'sidebarFadeInIds'),
  setHistory: make(set, 'history'),
  reset: () => set(makeInitial()),
}));

export const setCollections = (v) => useCollectionsStore.getState().setCollections(v);
export const setSchemas = (v) => useCollectionsStore.getState().setSchemas(v);
export const setSelectedRequest = (v) => useCollectionsStore.getState().setSelectedRequest(v);
export const setSelectedCollection = (v) => useCollectionsStore.getState().setSelectedCollection(v);
export const setSelectedFolder = (v) => useCollectionsStore.getState().setSelectedFolder(v);
export const setEditingItem = (v) => useCollectionsStore.getState().setEditingItem(v);
export const setEditingSchemaItem = (v) => useCollectionsStore.getState().setEditingSchemaItem(v);
export const setSelectedSchemaEntry = (v) => useCollectionsStore.getState().setSelectedSchemaEntry(v);
export const setSidebarFadeInIds = (v) => useCollectionsStore.getState().setSidebarFadeInIds(v);
export const setHistory = (v) => useCollectionsStore.getState().setHistory(v);
